import {useEffect, useState} from 'react';
import CreateTicketSalesCapForm, {
    CreateTicketSalesCapFormValues,
} from 'src/app/components/forms/CreateTicketSalesCapForm';
import useApiFetch from 'src/app/hooks/useApiFetch';
import CreateDataResponse from 'src/data/api/common/CreateDataResponse';
import ApiResponseBody from 'src/data/api/responses/ApiResponseBody';
import {EmptyBody} from 'src/data/models/common/emptyBody';
import Price from 'src/data/models/common/price';
import {TicketSalesCapSalesProfile} from 'src/data/models/sales-profile/TicketSalesCapSalesProfile';
import ticketSalesCapService, {
    CreateTicketsSalesCapDto,
    TicketSalesCap,
} from 'src/data/services/ticketSalesCapService';
import {defaultCurrency} from 'src/shared/currencies';

const toTicketSalesCapSalesProfile = (salesProfileId: string, price: Price): TicketSalesCapSalesProfile => ({
    salesProfileId,
    price: {currency: price.currency, value: Number(price.value)},
});

const isSamePrice = (left: Price, right: Price) => left.currency === right.currency && left.value === right.value;

interface Props {
    eventId: string;
    id?: string;
    onSucceed: () => void;
}

export default function CreateTicketSalesCapFormDataWrapper({
                                                                eventId,
                                                                id,
                                                                onSucceed,
                                                            }: Props): JSX.Element {
    const [defaultFormValues, setDefaultFormValues] = useState<
        CreateTicketSalesCapFormValues | undefined
    >();

    const [{data, loading}, handleFetch] = useApiFetch<ApiResponseBody<TicketSalesCap>>();
    const [{loading: isCreating}, handleCreate] =
        useApiFetch<ApiResponseBody<CreateDataResponse>>();
    const [{loading: isUpdating}, handleUpdate] = useApiFetch<ApiResponseBody<EmptyBody>>();

    useEffect(() => {
        if (!id) return;

        handleFetch(ticketSalesCapService.getTicketSalesCapById(eventId, id), {
            useDefaultErrorHandler: true,
        });
    }, []);

    useEffect(() => {
        if (!id || !data) return;

        const {
            seatingPlanCategoryId,
            seatingPlanCategoryName,
            salesProfiles,
            expectedPurchasePrice,
            salesCap,
            fixedQuantityStep,
            bookingOptionId,
            bookingOptionName,
        } = data.data;

        const firstPrice = salesProfiles[0]?.price;

        const formValues: CreateTicketSalesCapFormValues = {
            seatingPlanCategory: {
                label: seatingPlanCategoryName,
                value: seatingPlanCategoryId,
            },
            bookingOption: {label: bookingOptionName, value: bookingOptionId},
            useSamePrice: salesProfiles.every(({price}) => firstPrice !== undefined && isSamePrice(price, firstPrice)),
            retailPrice: firstPrice ?? {value: undefined, currency: defaultCurrency},
            salesProfilePrices: salesProfiles.map(({salesProfileId, price}) => ({salesProfileId, price})),
            expectedPurchasePrice: expectedPurchasePrice,
            fixedQuantityStep,
            salesCap,
            salesProfiles: salesProfiles.map(({salesProfileId, name}) => ({label: name, value: salesProfileId})),
        };

        setDefaultFormValues(formValues);
    }, [data]);

    const onFormSubmit = (values: CreateTicketSalesCapFormValues) => {
        const {
            seatingPlanCategory,
            useSamePrice,
            retailPrice,
            salesProfilePrices,
            expectedPurchasePrice,
            salesCap,
            fixedQuantityStep,
            bookingOption,
        } = values;

        const dto: CreateTicketsSalesCapDto = {
            seatingPlanCategoryId: seatingPlanCategory?.value,
            bookingOptionId: bookingOption.value,
            salesProfiles: salesProfilePrices.map(({salesProfileId, price}) =>
                toTicketSalesCapSalesProfile(salesProfileId, useSamePrice ? retailPrice : price)
            ),
            expectedPurchasePrice,
            salesCap,
            fixedQuantityStep,
        };

        if (id) {
            handleUpdate(ticketSalesCapService.updateTicketsSalesCap(eventId, id, dto), {
                useDefaultErrorHandler: true,
                onSuccess: () => onSucceed?.(),
            });

            return;
        }

        handleCreate(ticketSalesCapService.createTicketsSalesCap(eventId, dto), {
            useDefaultErrorHandler: true,
            onSuccess: () => onSucceed?.(),
        });
    };

    return (
        <CreateTicketSalesCapForm
            eventId={eventId}
            editMode={!!id}
            loading={loading || isCreating || isUpdating}
            defaultValues={defaultFormValues}
            onFormSubmit={onFormSubmit}
        />
    );
}
